/**
 * B2C Consumer Authentication Service
 *
 * This service handles authentication for consumer/borrower users via
 * Stytch Consumer API (not B2B). All endpoints use the /api/v1/b2c/auth/*
 * prefix.
 *
 * Key differences from B2B (stytchAuthService.js):
 * - Uses Stytch Consumer project (separate from B2B)
 * - No organization membership required
 * - Tenant context passed via signed state in redirect URL
 */
import axios from 'axios';
import { api } from 'constants/constant';

const CURRENT_CONSUMER_TTL_MS = 60_000;
let currentConsumerCache = null;
let currentConsumerCachedAt = 0;
let currentConsumerInflight = null;

// Axios client for B2C (Consumer) authentication endpoints
const b2cAuthApi = axios.create({
  baseURL: api,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Attach tenant headers for B2C requests
b2cAuthApi.interceptors.request.use(
  (config) => {
    if (typeof window !== 'undefined') {
      config.headers['X-Tenant-Host'] = window.location.hostname;

      const searchParams = new URLSearchParams(window.location.search || '');
      const slug = searchParams.get('slug');
      const branch = searchParams.get('branch') || searchParams.get('branch_slug');

      if (slug) {
        config.headers['X-Tenant-Slug'] = slug.toLowerCase();
      }
      if (branch) {
        config.headers['X-Branch-Slug'] = branch.toLowerCase();
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Extract tenant context from URL or session storage
 */
export const getTenantContext = () => {
  if (typeof window === 'undefined') {
    return { organizationId: null, branchId: null, loanOfficerId: null };
  }

  const searchParams = new URLSearchParams(window.location.search || '');

  let organizationId = searchParams.get('org_id') || searchParams.get('organization_id');
  let branchId = searchParams.get('branch_id');
  let loanOfficerId = searchParams.get('lo_id') || searchParams.get('loan_officer_id');
  let slug = searchParams.get('slug');
  let branchSlug = searchParams.get('branch') || searchParams.get('branch_slug');

  if (typeof sessionStorage !== 'undefined') {
    if (!organizationId) {
      organizationId = sessionStorage.getItem('b2c_organization_id');
    }
    if (!branchId) {
      branchId = sessionStorage.getItem('b2c_branch_id');
    }
    if (!loanOfficerId) {
      loanOfficerId = sessionStorage.getItem('b2c_loan_officer_id');
    }
    if (!slug) {
      slug = sessionStorage.getItem('mira_client_slug');
    }
    if (!branchSlug) {
      branchSlug = sessionStorage.getItem('mira_branch_slug');
    }
  }

  return {
    organizationId,
    branchId,
    loanOfficerId,
    slug,
    branchSlug,
  };
};

/**
 * Store tenant context in session storage for persistence across navigation
 */
export const storeTenantContext = (context) => {
  if (typeof sessionStorage === 'undefined') return;

  if (context.organizationId) {
    sessionStorage.setItem('b2c_organization_id', context.organizationId);
  }
  if (context.branchId) {
    sessionStorage.setItem('b2c_branch_id', context.branchId);
  }
  if (context.loanOfficerId) {
    sessionStorage.setItem('b2c_loan_officer_id', context.loanOfficerId);
  }
};

/**
 * Validate tenant (org/branch) slugs before loading the login page.
 */
export const validateTenant = (orgSlug, branchSlug = null) => {
  const params = { org_slug: orgSlug };
  if (branchSlug) {
    params.branch_slug = branchSlug;
  }
  return b2cAuthApi.get('/b2c/auth/validate-tenant', { params });
};

/**
 * Send magic link to consumer email
 */
export const sendConsumerMagicLink = (email, metadata = {}) => {
  const tenantContext = getTenantContext();
  const organizationId = metadata.organizationId || tenantContext.organizationId;
  const branchId = metadata.branchId || tenantContext.branchId;
  const loanOfficerId = metadata.loanOfficerId || tenantContext.loanOfficerId;
  const organizationSlug = metadata.organizationSlug || tenantContext.slug;
  const branchSlug = metadata.branchSlug || tenantContext.branchSlug;

  return b2cAuthApi.post('/b2c/auth/magic-link/send', {
    email,
    organization_id: organizationId,
    branch_id: branchId,
    loan_officer_id: loanOfficerId,
    organization_slug: organizationSlug,
    branch_slug: branchSlug,
  });
};

/**
 * Authenticate consumer magic link token
 */
export const authenticateConsumerToken = (token, statePayload = {}) => {
  return b2cAuthApi.post('/b2c/auth/magic-link/authenticate', {
    token,
    ...statePayload,
  });
};

/**
 * Get current consumer user
 */
export const clearCurrentConsumerCache = () => {
  currentConsumerCache = null;
  currentConsumerCachedAt = 0;
  currentConsumerInflight = null;
};

export const fetchConsumerUser = ({ forceRefresh = false } = {}) => {
  const now = Date.now();

  if (!forceRefresh && currentConsumerCache && now - currentConsumerCachedAt < CURRENT_CONSUMER_TTL_MS) {
    return Promise.resolve(currentConsumerCache);
  }

  if (!forceRefresh && currentConsumerInflight) {
    return currentConsumerInflight;
  }

  currentConsumerInflight = b2cAuthApi
    .get('/b2c/auth/me')
    .then((resp) => {
      currentConsumerCache = resp;
      currentConsumerCachedAt = Date.now();
      return resp;
    })
    .finally(() => {
      currentConsumerInflight = null;
    });

  return currentConsumerInflight;
};

export const fetchCurrentConsumer = fetchConsumerUser;

/**
 * Revoke consumer session (logout)
 */
export const revokeConsumerSession = () => {
  clearCurrentConsumerCache();
  return b2cAuthApi.post('/b2c/auth/session/revoke', {});
};

/**
 * Complete consumer onboarding
 */
export const completeConsumerOnboarding = (payload) => {
  return b2cAuthApi.post('/b2c/auth/complete-onboarding', payload);
};

export const completeCoBorrowerOnboarding = (payload) => {
  return b2cAuthApi.post('/b2c/loan/co_borrower/complete-onboarding', payload);
};

/**
 * Exchange Stytch Consumer session token for MIRA JWT (B2C flow)
 */
export const exchangeB2CSession = (payload) => {
  clearCurrentConsumerCache();
  return b2cAuthApi.post('/b2c/auth/session/exchange', {
    token: payload.token,
    stytch_token_type: payload.stytch_token_type || 'sessions',
    organization_id: payload.organization_id,
    branch_id: payload.branch_id,
  });
};

export default {
  sendConsumerMagicLink,
  authenticateConsumerToken,
  fetchConsumerUser,
  fetchCurrentConsumer,
  clearCurrentConsumerCache,
  revokeConsumerSession,
  completeConsumerOnboarding,
  completeCoBorrowerOnboarding,
  exchangeB2CSession,
  getTenantContext,
  storeTenantContext,
  validateTenant,
};
